import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { Button } from '@kamalkari/ui';
import { KolamMandala, KolamCorner } from './KolamPattern';

export function HeroBanner() {
  return (
    <section className="relative min-h-[90vh] bg-warm-50 overflow-hidden">
      {/* Kolam pattern decorations */}
      <KolamMandala className="absolute top-0 left-0 w-64 h-64 text-warm-300 -translate-x-1/3 -translate-y-1/3 opacity-50" />
      <KolamMandala className="absolute bottom-0 right-0 w-96 h-96 text-warm-300 translate-x-1/3 translate-y-1/3 opacity-40" />
      <KolamCorner className="absolute top-8 right-8 w-24 h-24 text-warm-400 opacity-60" />
      <KolamCorner className="absolute bottom-8 left-8 w-24 h-24 text-warm-400 opacity-60 rotate-180" />

      <div className="relative container-page py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[70vh]">
          {/* Left side - Text content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-block mb-6"
            >
              <span className="text-xs font-medium tracking-[0.3em] uppercase text-ochre border border-ochre/30 px-4 py-2 rounded-full">
                Art Gallery
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-serif text-warm-900 leading-[1.1] mb-6"
            >
              Kamalkari
              <span className="block text-2xl sm:text-3xl lg:text-4xl text-warm-600 font-normal mt-3">
                by Aarti
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="w-24 h-0.5 bg-gradient-to-r from-ochre to-madder mb-6"
            />

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xl sm:text-2xl text-warm-700 font-serif leading-relaxed mb-6"
            >
              A sacred art gallery of kolams, colours & conscious gifting.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-warm-600 text-lg mb-8 max-w-lg"
            >
              Each piece is hand-drawn with intention, transforming ancient South Indian
              kolam traditions into contemporary art for your everyday life.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <Button asChild size="lg" className="bg-warm-900 text-warm-50 hover:bg-warm-800">
                <Link to="/products">Enter Gallery</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-warm-400 text-warm-700 hover:bg-warm-100">
                <Link to="/about">Meet the Artist</Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap gap-8 text-sm text-warm-600"
            >
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-ochre" />
                <span>Hand-drawn Art</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-madder" />
                <span>Small Batches</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-indigo" />
                <span>Conscious Gifting</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right side - Gallery display */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2 relative"
          >
            <div className="relative">
              {/* Main featured image - gallery frame style */}
              <div className="relative bg-white p-3 sm:p-4 shadow-2xl rounded-sm">
                <div className="absolute inset-0 border border-warm-200 rounded-sm" />
                <img
                  src="/products/mugs.jpg"
                  alt="Kolam Art Collection"
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-4 rounded">
                  <p className="text-xs uppercase tracking-wider text-warm-500 mb-1">Featured Collection</p>
                  <p className="font-serif text-warm-900">Ceramic Mugs with Kolam Art</p>
                </div>
              </div>

              {/* Secondary floating image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute -bottom-8 -left-8 w-32 sm:w-40 bg-white p-2 shadow-xl rounded-sm hidden md:block"
              >
                <img
                  src="/products/calendars.jpg"
                  alt="Desk Calendars"
                  className="w-full aspect-square object-cover"
                />
              </motion.div>

              {/* Third floating image */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute -top-6 -right-6 w-28 sm:w-36 bg-white p-2 shadow-xl rounded-sm hidden md:block"
              >
                <img
                  src="/products/notebooks-frames.jpg"
                  alt="Wall Frames"
                  className="w-full aspect-square object-cover"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom decorative border */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-ochre/30 to-transparent" />
    </section>
  );
}
